import { Typography } from "@mui/material";
import { formatOcpiCurrency } from "src/utils/formatTime";

export const ANALYTICS_TABS = [
  { value: "transactions", label: "Transactions", icon: "eva:activity-fill" },
  { value: "revenue", label: "Revenue", icon: "eva:credit-card-fill" },
  // { value: "partnerTransactions", label: "Partner Transactions", icon: "eva:swap-fill" },
  { value: "roaming", label: "Roaming Analytics", icon: "eva:globe-fill" }
];

// API Endpoints
export const ANALYTICS_API = {
  BASE_URL: '/cms/v2/ocpi/module/analytics/analytics',
  TYPES: '/cms/v2/ocpi/module/analytics/analytics/types',
  METADATA: '/cms/v2/ocpi/module/analytics/analytics/metadata',
  CHARTS: '/cms/v2/ocpi/module/analytics/analytics/charts',
  HEALTH: '/cms/v2/ocpi/module/analytics/analytics/health',
  // New separate endpoints
  TRANSACTIONS: '/cms/v2/ocpi/module/analytics/analytics/transactions',
  REVENUE: '/cms/v2/ocpi/module/analytics/analytics/revenue',
  PARTNERS: '/cms/v2/ocpi/module/analytics/analytics/partners',
  ROAMING: '/cms/v2/ocpi/module/analytics/analytics/roaming'
};

// Dynamic configuration that will be populated from API
export const DYNAMIC_CONFIG = {
  analyticsTypes: [],
  chartTypes: [],
  dataTypes: [],
  availableFilters: {},
  supportedFormats: {},
  cacheInfo: {}
};

// Default mock data (fallback when API is not available)
export const MOCK_DATA = {
  kpis: {
    totalRevenue: 45230,
    totalTransactions: 8456,
    activePartners: 15,
    successRate: 95.2,
    totalEnergy: 45230,
    avgSessionDuration: 45
  },
  trends: {
    revenue: { current: 12500, previous: 10850, change: 15.3 },
    transactions: { current: 1234, previous: 1136, change: 8.7 },
    partners: { current: 15, previous: 13, change: 15.4 },
    successRate: { current: 95.2, previous: 94.0, change: 1.2 },
    energy: { current: 2450, previous: 2185, change: 12.1 },
    sessionDuration: { current: 45, previous: 46, change: -2.3 }
  },
  revenueAnalytics: {
    totalRevenue: 45230,
    ourShareRevenue: 13569,
    totalTransactions: 8456,
    avgShareRate: 30.0,
    partnerRevenue: [
      { id: 1, name: 'Partner A', totalRevenue: 8500, ourShare: 2550, shareRate: 30.0, transactions: 1200 },
      { id: 2, name: 'Partner B', totalRevenue: 7200, ourShare: 2160, shareRate: 30.0, transactions: 980 },
      { id: 3, name: 'Partner C', totalRevenue: 6800, ourShare: 2040, shareRate: 30.0, transactions: 890 },
      { id: 4, name: 'Partner D', totalRevenue: 5200, ourShare: 1560, shareRate: 30.0, transactions: 750 },
      { id: 5, name: 'Partner E', totalRevenue: 4700, ourShare: 1410, shareRate: 30.0, transactions: 396 }
    ],
    monthlyRevenue: [
      { month: 'Jan', total: 12000, ourShare: 3600, transactions: 1200 },
      { month: 'Feb', total: 13500, ourShare: 4050, transactions: 1350 },
      { month: 'Mar', total: 9800, ourShare: 2940, transactions: 980 },
      { month: 'Apr', total: 16500, ourShare: 4950, transactions: 1650 },
      { month: 'May', total: 14200, ourShare: 4260, transactions: 1420 },
      { month: 'Jun', total: 18900, ourShare: 5670, transactions: 1890 }
    ],
    revenueByType: [
      { type: 'Energy', revenue: 28000, ourShare: 8400, percentage: 62.0 },
      { type: 'Session', revenue: 12000, ourShare: 3600, percentage: 26.5 },
      { type: 'Other', revenue: 5230, ourShare: 1569, percentage: 11.5 }
    ]
  },
  partnerTransactions: {
    outgoing: {
      total: 3240,
      successful: 3080,
      failed: 160,
      successRate: 95.1,
      trend: 8.5
    },
    incoming: {
      total: 5216,
      successful: 4955,
      failed: 261,
      successRate: 95.0,
      trend: 12.3
    },
    transactionTypes: {
      startTransaction: { count: 2840, successRate: 96.2, color: '#00C851' },
      stopTransaction: { count: 2180, successRate: 94.8, color: '#FF6900' },
      remoteStart: { count: 1560, successRate: 93.5, color: '#0074D9' },
      remoteStop: { count: 876, successRate: 95.8, color: '#9933CC' }
    },
    byPartner: [
      {
        name: 'Partner A',
        outgoing: { total: 850, successful: 810, failed: 40, successRate: 95.3 },
        incoming: { total: 1200, successful: 1140, failed: 60, successRate: 95.0 }
      },
      {
        name: 'Partner B',
        outgoing: { total: 720, successful: 685, failed: 35, successRate: 95.1 },
        incoming: { total: 980, successful: 931, failed: 49, successRate: 95.0 }
      },
      {
        name: 'Partner C',
        outgoing: { total: 680, successful: 645, failed: 35, successRate: 94.9 },
        incoming: { total: 890, successful: 845, failed: 45, successRate: 95.0 }
      },
      {
        name: 'Partner D',
        outgoing: { total: 520, successful: 495, failed: 25, successRate: 95.2 },
        incoming: { total: 750, successful: 712, failed: 38, successRate: 94.9 }
      },
      {
        name: 'Partner E',
        outgoing: { total: 470, successful: 445, failed: 25, successRate: 94.7 },
        incoming: { total: 396, successful: 376, failed: 20, successRate: 94.9 }
      }
    ],
    monthlyTrends: [
      { month: 'Jan', outgoing: 280, incoming: 420, outgoingSuccess: 95.2, incomingSuccess: 95.1 },
      { month: 'Feb', outgoing: 295, incoming: 445, outgoingSuccess: 95.5, incomingSuccess: 95.3 },
      { month: 'Mar', outgoing: 310, incoming: 465, outgoingSuccess: 95.8, incomingSuccess: 95.6 },
      { month: 'Apr', outgoing: 325, incoming: 485, outgoingSuccess: 96.1, incomingSuccess: 95.8 },
      { month: 'May', outgoing: 340, incoming: 510, outgoingSuccess: 96.3, incomingSuccess: 96.0 },
      { month: 'Jun', outgoing: 355, incoming: 535, outgoingSuccess: 96.5, incomingSuccess: 96.2 }
    ]
  },
  roamingAnalytics: {
    kpis: {
      roamingSessions: 2840,
      activeTokens: 1560,
      totalEnergy: 45230,
      avgUtilization: 78.5
    },
    trends: {
      roamingSessions: { current: 2840, previous: 2650, change: 7.2 },
      activeTokens: { current: 1560, previous: 1420, change: 9.9 },
      energy: { current: 45230, previous: 41850, change: 8.1 },
      utilization: { current: 78.5, previous: 75.2, change: 4.4 }
    },
    roamingActivity: [
      { month: 'Jan', sessions: 420, users: 380, energy: 7200 },
      { month: 'Feb', sessions: 445, users: 395, energy: 7650 },
      { month: 'Mar', sessions: 465, users: 410, energy: 8100 },
      { month: 'Apr', sessions: 485, users: 425, energy: 8550 },
      { month: 'May', sessions: 510, users: 440, energy: 9000 },
      { month: 'Jun', sessions: 535, users: 455, energy: 9450 }
    ],
    energyConsumption: [
      { month: 'Jan', energy: 7200, utilization: 72.0 },
      { month: 'Feb', energy: 7650, utilization: 73.5 },
      { month: 'Mar', energy: 8100, utilization: 75.0 },
      { month: 'Apr', energy: 8550, utilization: 76.5 },
      { month: 'May', energy: 9000, utilization: 78.0 },
      { month: 'Jun', energy: 9450, utilization: 79.5 }
    ],
    topLocations: [
      { location: 'Downtown Station A', totalSessions: 450, avgDuration: 45, utilization: 85.2 },
      { location: 'Airport Hub B', totalSessions: 380, avgDuration: 52, utilization: 78.9 },
      { location: 'Shopping Mall C', totalSessions: 320, avgDuration: 38, utilization: 72.4 },
      { location: 'Business Park D', totalSessions: 290, avgDuration: 41, utilization: 68.7 },
      { location: 'Residential Area E', totalSessions: 260, avgDuration: 35, utilization: 65.3 }
    ]
  }
};

export const REVENUE_TABLE_COLUMNS = (a, b) => [
  { field: 'partner', headerName: 'Partner', flex: 1, minWidth: 150 },
  { 
    field: 'totalRevenue', 
    headerName: 'Total Revenue', 
    flex: 1, 
    minWidth: 140, 
    renderCell: (params) => (
      <Typography color={a}>{formatOcpiCurrency(params.value)}</Typography>
    )
  },
  { 
    field: 'ourShare', 
    headerName: 'Our Share', 
    flex: 1, 
    minWidth: 140, 
    renderCell: (params) => (
      <Typography color={b}>{formatOcpiCurrency(params.value)}</Typography>
    )
  },
  { field: 'shareRate', headerName: 'Share Rate (%)', flex: 1, minWidth: 120, renderCell: (params) => `${params.value}%` },
  { field: 'totalTransactions', headerName: 'Transactions', flex: 1, minWidth: 120 }
];

export const PARTNER_TRANSACTIONS_TABLE_COLUMNS = [
  { field: 'partner', headerName: 'Partner Name', flex: 1, minWidth: 150 },
  { field: 'outgoingTotal', headerName: 'Outgoing Total', flex: 1, minWidth: 120 },
  { field: 'outgoingSuccess', headerName: 'Outgoing Success', flex: 1, minWidth: 140 },
  { field: 'incomingTotal', headerName: 'Incoming Total', flex: 1, minWidth: 120 },
  { field: 'incomingSuccess', headerName: 'Incoming Success', flex: 1, minWidth: 140 },
  { field: 'overallSuccess', headerName: 'Overall Success', flex: 1, minWidth: 140 }
];

export const TOP_LOCATIONS_TABLE_COLUMNS = [
  { field: 'locationName', headerName: 'Location', flex: 1, minWidth: 200 },
  { field: 'totalSessions', headerName: 'Total Sessions', flex: 1, minWidth: 140 },
  { field: 'avgDurationMin', headerName: 'Avg Duration (min)', flex: 1, minWidth: 160 },
  { field: 'utilizationPercentage', headerName: 'Utilization (%)', flex: 1, minWidth: 140, renderCell: (params) => `${params.value}%` }
];

// Helper functions that work with the existing useList hook data
export const getCompatibleCharts = (dataType, analyticsTypes = []) => {
  const dataTypeConfig = analyticsTypes.find(dt => dt.type === dataType);
  return dataTypeConfig ? dataTypeConfig.supportedCharts : [];
};

export const getAvailableFilters = (analyticsType, analyticsTypes = []) => {
  const typeConfig = analyticsTypes.find(at => at.type === analyticsType);
  return typeConfig ? typeConfig.filters : [];
};

export const validateAnalyticsRequest = (request, analyticsTypes = []) => {
  const errors = [];
  
  // Check if analytics type is valid
  const validTypes = analyticsTypes.map(at => at.type);
  if (request.types && Array.isArray(request.types)) {
    const invalidTypes = request.types.filter(t => !validTypes.includes(t));
    if (invalidTypes.length > 0) {
      errors.push(`Invalid analytics types: ${invalidTypes.join(', ')}. Valid types: ${validTypes.join(', ')}`);
    }
  } else if (request.type && !validTypes.includes(request.type)) {
    errors.push(`Invalid analytics type: ${request.type}. Valid types: ${validTypes.join(', ')}`);
  }
  
  return errors;
};
